<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-cells>
        <h2 class="title">Gross</h2>
        <app-filters
          v-model="status_filter"
          :filters="status_filters"
          name="filterStatus"
          @change="fetchItems"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #month="{ row }">{{ dateFormat(row.month) }}</template>
        <template #contract="{ row }">#{{ row.contract }}</template>
        <template #basic="{ row }">{{ row.basic.toLocaleString() }} TSh</template>
        <template #allowance="{ row }">{{ row.allowance.toLocaleString() }} TSh</template>
        <template #total_warnings="{ row }">{{ row.total_warnings.toLocaleString() }} TSh</template>
        <template #total_benefits="{ row }">{{ row.total_benefits.toLocaleString() }} TSh</template>
        <template #gross="{ row }">{{ row.gross ? `${ row.gross.toLocaleString() } TSh` : '-' }}</template>
        <template #total_advances="{ row }">{{ row.total_advances.toLocaleString() }} TSh</template>
      </v-client-table>
      <app-cells :position="pagination.pages > 1 ? 'between' : 'end'">
        <paginate
          :clickHandler="onClickPagination"
          v-if="pagination.pages > 1"
          v-model="pagination.page"
          :page-count="pagination.pages"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'pagination__item'"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :prev-link-class="'pagination__link pagination__link--prev'"
        />
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" :limit="10"/>
      </app-cells>
      <app-cells>
        <h2 class="title">Accrued salary</h2>
      </app-cells>
      <v-client-table
        :data="table_salary.items"
        :columns="table_salary.columns"
        :options="table_salary.options"
        class="table-default"
        :class="{ 'table-default--loading': table_salary_preloader }"
      >
        <template #month="{ row }">
          <app-cells :indent="false">
            <div class="table-default__controls">
              <button
                v-if="!print_preload || print_preload !== `print_${row.id}`"
                @click="onPrint(row)"
                type="button"
                :disabled="print_preload"
              >
                <img src="@/assets/img/print-table-icon.svg" alt="">
              </button>
              <img v-show="print_preload === `print_${row.id}`" src="@/assets/img/preloader-export.svg" alt="">
            </div>
            {{ dateFormat(row.payroll.month) }}
          </app-cells>
        </template>
        <template #basic="{ row }">{{ row.employee_month.basic.toLocaleString() }}</template>
        <template #allowance="{ row }">{{ row.employee_month.allowance.toLocaleString() }}</template>
        <template #penalties="{ row }">
          {{ row.employee_month.total_warnings > 0 ? '-' : '' }}{{ row.employee_month.total_warnings.toLocaleString() }}
        </template>
        <template #bonuses="{ row }">
          {{ row.employee_month.total_benefits.toLocaleString() }}
        </template>
        <template #gross="{ row }">{{ row.gross.toLocaleString() }}</template>
        <template #nssf="{ row }">
          <span
            :class="{ 'status-tag': !row.employee_month.employee.nssf_id }"
            :style="!row.employee_month.employee.nssf_id ? 'backgroundColor: #e85c20' : ''"
            v-tooltip="{ content: 'Employee / Employer', placement: 'top-start', offset: 10 }"
          >
            {{ row.nssf_employee.toLocaleString() }} / {{ row.nssf_employer.toLocaleString() }}
          </span>
        </template>
        <template #nhif="{ row }">
          <span
            :class="{ 'status-tag': row.employee_month.employee.nhif_payer && !row.employee_month.employee.nhif_id }"
            :style="row.employee_month.employee.nhif_payer && !row.employee_month.employee.nhif_id ? 'backgroundColor: #e85c20' : ''"
            v-tooltip="{ content: 'Employee / Employer', placement: 'top-start', offset: 10 }"
          >
            {{ row.nhif_employee.toLocaleString() }} / {{ row.nhif_employer.toLocaleString() }}
          </span>
        </template>
        <template #paye="{ row }">
          <span
            :class="{ 'status-tag': !row.employee_month.employee.tin }"
            :style="!row.employee_month.employee.tin ? 'backgroundColor: #e85c20' : ''"
          >
            {{ row.paye.toLocaleString() }}
          </span>
        </template>
        <template #net_salary="{ row }">{{ row.net_salary.toLocaleString() }}</template>
        <template #advance_amount="{ row }">{{ row.advance_amount.toLocaleString() }}</template>
        <template #heslb_loan_amount="{ row }">{{ row.heslb_loan_amount.toLocaleString() }}</template>
        <template #staff_loan_amount="{ row }">{{ row.staff_loan_amount.toLocaleString() }}</template>
        <template #accrued_salary="{ row }">{{ row.accrued_salary.toLocaleString() }}</template>
      </v-client-table>
      <app-cells :position="pagination_salary.pages > 1 ? 'between' : 'end'">
        <paginate
          :clickHandler="onClickSalaryPagination"
          v-if="pagination_salary.pages > 1"
          v-model="pagination_salary.page"
          :page-count="pagination_salary.pages"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'pagination__item'"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :prev-link-class="'pagination__link pagination__link--prev'"
        />
        <app-table-records :all="pagination_salary.count" :count="table_salary.items.length"
                           :page="pagination_salary.page" :limit="5"/>
      </app-cells>
      <app-cells><h2 class="title">Advances</h2></app-cells>
      <v-client-table
        :data="table_advances.items"
        :columns="table_advances.columns"
        :options="table_advances.options"
        class="table-default"
        :class="{ 'table-default--loading': table_advances_preloader }"
      >
        <template #h__id>
          <div class="table-default__left">Request</div>
        </template>
        <template #h__employee>
          <div class="table-default__left">Employee</div>
        </template>
        <template #id="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'employees-advance-requests-detail', params: { id: row.id } }"
              class="table-default__link-border"
            >
              Request - #{{ row.id }}
            </router-link>
          </div>
        </template>
        <template #employee="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'employees-profile', params: { id: row.employee.id } }"
              class="table-default__link-border"
            >
              {{ row.employee.full_name }}
            </router-link>
          </div>
        </template>
        <template #amount="{ row }">{{ row.amount.toLocaleString() }}</template>
        <template #installments="{ row }">{{ row.installments }}</template>
        <template #paid="{ row }">{{ row.paid_amount.toLocaleString() }} TSh</template>
        <template #r_installments="{ row }">{{ row.remain_installments }}</template>
        <template #unpaid_amount="{ row }">{{ row.unpaid_amount.toLocaleString() }} TSh</template>
      </v-client-table>
      <app-cells :position="pagination_advances.pages > 1 ? 'between' : 'end'">
        <paginate
          :clickHandler="onClickAdvancesPagination"
          v-if="pagination_advances.pages > 1"
          v-model="pagination_advances.page"
          :page-count="pagination_advances.pages"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'pagination__item'"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :prev-link-class="'pagination__link pagination__link--prev'"
        />
        <app-table-records
          :all="pagination_advances.count"
          :count="table_advances.items.length"
          :page="pagination_advances.page"
          :limit="10"
        />
      </app-cells>
    </template>
  </div>
</template>

<script>
import {
  getEmployeesAdvanceRequestsList,
  getEmployeesExportPaymentSlip,
  getEmployeesMonthCalculation,
  getEmployeesPayrollEmployees,
} from '@/http'
import { format, parse } from 'date-fns'

export default {
  name: 'EmployeeCalculation',
  data() {
    return {
      is_loaded: false,
      table_preloader: true,
      status_filter: 'upcoming',
      status_filters: [
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'archive', label: 'Archive' },
      ],
      table: {
        items: [],
        columns: ['month', 'contract', 'basic', 'allowance', 'total_warnings', 'total_benefits', 'gross', 'total_advances'],
        options: {
          headings: {
            month: 'Month',
            contract: 'Contract',
            basic: 'Basic',
            allowance: 'Allowance',
            total_warnings: 'Warnings',
            total_benefits: 'Benefits',
            gross: 'Gross',
            total_advances: 'Advances',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 10,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_salary_preloader: false,
      table_salary: {
        items: [],
        columns: [
          'month',
          'basic',
          'allowance',
          'penalties',
          'bonuses',
          'gross',
          'nssf',
          'nhif',
          'paye',
          'wht',
          'net_salary',
          'advance_amount',
          'heslb_loan_amount',
          'staff_loan_amount',
          'accrued_salary',
        ],
        options: {
          headings: {
            month: 'Month',
            basic: 'Basic',
            allowance: 'Allowance',
            penalties: 'Penalties',
            bonuses: 'Benefits',
            gross: 'Gross',
            nssf: 'NSSF',
            nhif: 'NHIF',
            paye: 'PAYE',
            wht: 'WHT',
            net_salary: 'Net salary',
            advance_amount: 'Advance',
            heslb_loan_amount: 'HESLB',
            staff_loan_amount: 'Staff loan',
            accrued_salary: 'Accrued salary',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 5,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_salary: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_advances_preloader: false,
      table_advances: {
        items: [],
        columns: ['id', 'employee', 'amount', 'installments', 'paid', 'r_installments', 'unpaid_amount'],
        options: {
          headings: {
            id: 'Request',
            employee: 'Employee',
            amount: 'Amount',
            installments: 'Installments',
            paid: 'Paid',
            r_installments: 'Remaining installments',
            unpaid_amount: 'Unpaid',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 10,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_advances: {
        pages: 1,
        page: 1,
        count: 1,
      },
      print_preload: null,
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    Promise.all([
      getEmployeesMonthCalculation(
        this.$route.params.id,
        {
          page: this.pagination.page,
          page_size: 10,
          category: this.status_filter,
        }
      ),
      getEmployeesPayrollEmployees(
        {
          page: this.pagination.page,
          page_size: 5,
          employee: this.$route.params.id,
        }
      ),
      getEmployeesAdvanceRequestsList({
        page: this.pagination_advances.page,
        page_size: 10,
        status: 'paid',
        employee: this.$route.params.id
      })
    ])
      .then(res => {
        this.table.items = res[0].data.results
        this.pagination.pages = res[0].data.pages
        this.pagination.count = res[0].data.count

        this.table_salary.items = res[1].data.results
        this.pagination_salary.pages = res[1].data.pages
        this.pagination_salary.count = res[1].data.count

        this.table_advances.items = res[2].data.results
        this.pagination_advances.pages = res[2].data.pages
        this.pagination_advances.count = res[2].data.count

        this.is_loaded = true
        this.table_preloader = false
      })
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      getEmployeesMonthCalculation(
        this.$route.params.id,
        {
          page: this.pagination.page,
          page_size: 10,
          category: this.status_filter,
        }
      ).then(res => {
        this.table.items = res.data.results
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.table_preloader = false
      })
    },
    fetchSalaryItems() {
      this.table_salary_preloader = true
      getEmployeesPayrollEmployees(
        {
          page: this.pagination_salary.page,
          page_size: 5,
          employee: this.$route.params.id,
        }
      ).then(res => {
        this.table_salary.items = res.data.results
        this.pagination_salary.pages = res.data.pages
        this.pagination_salary.count = res.data.count
        this.table_salary_preloader = false
      })
    },
    fetchAdvancesItems() {
      getEmployeesAdvanceRequestsList({
        page: this.pagination_advances.page,
        page_size: 10,
        status: 'paid',
        employee: this.$route.params.id
      })
        .then(res => {
          this.table_advances.items = res.data.results
          this.pagination_advances.pages = res.data.pages
          this.pagination_advances.count = res.data.count
          this.table_advances_preloader = false
        })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onClickSalaryPagination(page) {
      this.pagination_salary.page = page
      this.fetchSalaryItems()
    },
    onClickAdvancesPagination(page) {
      this.pagination_advances.page = page
      this.fetchAdvancesItems()
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy')
    },
    onPrint(row) {
      this.print_preload = `print_${ row.id }`
      getEmployeesExportPaymentSlip(row.id)
        .finally(() => { this.print_preload = null })
        .then(response => { window.open(response.data) })
    },
  }
}
</script>
